'use client';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ExternalLink, Menu } from 'lucide-react';
import { Button } from '../ui/button';
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger, } from '../ui/sheet';
import { HelpCenterNavButton } from '../ui-custom/help-center-nav-button';
import { ThemeToggle } from '../../contexts/theme-toggle';
import { UserNav } from '../blocks/navbar/user-nav';
import { LoginButton } from '../blocks/navbar/login-button';
import { cn } from '../../lib/utils';
var HELP_CENTER_URL = 'https://help.govinity.com/en/collections/19496614-public-portal-guides';
/**
 * Navigation bar component for portal pages.
 * Displays menu items, auth controls, and theme toggle.
 * Supports both desktop and mobile layouts.
 */
export function PortalNavigation(_a) {
    var menu = _a.menu, auth = _a.auth, userAvatarSlot = _a.userAvatarSlot, _b = _a.showThemeToggle, showThemeToggle = _b === void 0 ? false : _b, currentPath = _a.currentPath, searchSlot = _a.searchSlot, customNavLink = _a.customNavLink;
    var pathname = usePathname();
    var activePath = currentPath !== null && currentPath !== void 0 ? currentPath : pathname;
    var hasCustomNavLink = !!(customNavLink === null || customNavLink === void 0 ? void 0 : customNavLink.url) && !!(customNavLink === null || customNavLink === void 0 ? void 0 : customNavLink.label);
    return (<nav className="border-border bg-background relative z-10 border-b" role="navigation" aria-label="Main navigation">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-11 items-center justify-between">
          {/* Desktop Navigation */}
          <div className="hidden items-center space-x-1 md:flex">
            {menu.map(function (_a) {
            var url = _a.url, title = _a.title, icon = _a.icon;
            var isActive = activePath === url;
            return (<Link key={url} href={url} className={cn('flex items-center space-x-2 rounded-md px-4 py-2 text-sm font-medium transition-all', isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground')}>
                  {icon && <div className="h-4 w-4">{icon}</div>}
                  <span>{title}</span>
                </Link>);
        })}
          </div>

          {/* Right controls - Search, Account, Avatar, Theme Toggle */}
          <div className="hidden items-center space-x-2 md:flex">
            {hasCustomNavLink && (<Button asChild variant="ghost" size="sm">
                <Link href={customNavLink.url} className={cn('rounded-md font-medium transition-all', 'text-muted-foreground hover:bg-accent hover:text-foreground')} target="_blank" rel="noopener noreferrer">
                  {customNavLink.label}
                  <ExternalLink className="size-3.5"/>
                </Link>
              </Button>)}
            {searchSlot}
            {showThemeToggle && <ThemeToggle />}
            <HelpCenterNavButton href={HELP_CENTER_URL}/>

            {auth.showUserNav && (<div className="relative flex h-11 items-center">
                <div className="[&_button]:h-11 [&_button]:py-0">
                  <UserNav {...auth} userAvatarSlot={userAvatarSlot}/>
                </div>
              </div>)}

            {auth.login && <LoginButton loginUrl={auth.login.url}>{auth.login.text}</LoginButton>}

            {auth.signup && (<LoginButton loginUrl={auth.signup.url} isSignup variant="default">
                {auth.signup.text}
              </LoginButton>)}
          </div>

          {/* Mobile Navigation */}
          <div className="flex w-full items-center justify-between md:hidden">
            <span className="text-foreground text-sm font-medium">Menu</span>
            <div className="flex items-center space-x-1">
              {searchSlot}
              {showThemeToggle && <ThemeToggle />}
              <HelpCenterNavButton href={HELP_CENTER_URL}/>

              {auth.showUserNav && (<div className="relative flex h-11 items-center">
                  <div className="[&_button]:h-11 [&_button]:py-0">
                    <UserNav {...auth} userAvatarSlot={userAvatarSlot}/>
                  </div>
                </div>)}

              {auth.login && <LoginButton loginUrl={auth.login.url}>{auth.login.text}</LoginButton>}

              {auth.signup && (<LoginButton loginUrl={auth.signup.url} isSignup variant="default">
                  {auth.signup.text}
                </LoginButton>)}

              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Open mobile menu">
                    <Menu className="h-5 w-5"/>
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80">
                  <SheetHeader>
                    <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 flex flex-col space-y-4">
                    {menu.map(function (_a) {
            var url = _a.url, title = _a.title, icon = _a.icon;
            var isActive = activePath === url;
            return (<SheetClose key={url} asChild>
                          <Link href={url} className={cn('ml-4 flex items-center space-x-3 text-lg font-medium transition-colors', isActive ? 'text-primary' : 'text-foreground hover:text-primary')}>
                            {icon && <div className="h-5 w-5">{icon}</div>}
                            <span>{title}</span>
                          </Link>
                        </SheetClose>);
        })}
                    {hasCustomNavLink && (<SheetClose asChild>
                        <Link href={customNavLink.url} className={cn('ml-4 flex items-center space-x-3 text-lg font-medium transition-colors', 'text-foreground hover:text-primary')} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="size-4"/>
                          <span>{customNavLink.label}</span>
                        </Link>
                      </SheetClose>)}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </nav>);
}
